/* S1 Gift Packs — tab switching. Desktop tabs follow the WAI-ARIA tabs
   pattern (click + Left/Right/Home/End roving focus); switching a tab swaps
   the matching panel and crossfades the matching media image. Without JS
   the first pack stays visible (non-first panels ship with [hidden]).

   Mobile is a full-card snap carousel (Figma "Gift Packs — Mobile"): the
   cards track scrolls natively with the next card peeking; settling on a
   card selects that pack, and the prev / dots / next control row drives the
   track (prev/next wrap around). On first view the track nudges sideways
   once so the carousel is discoverable even at a glance. Desktop and mobile
   share the same select() state. */

const GP_MOBILE = window.matchMedia('(max-width: 767px)');

class S1GiftPacks extends HTMLElement {
  connectedCallback() {
    /* fade-ins: .s1-gp-fade + IntersectionObserver (same contract as .s1-ch-fade) */
    if (!this._fadeIo) {
      this._fadeIo = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add('is-visible');
            this._fadeIo.unobserve(en.target);
          }
        });
      }, { threshold: 0.2 });
    }
    this.querySelectorAll('.s1-gp-fade:not(.is-visible)').forEach((el) => this._fadeIo.observe(el));

    this.tabs = Array.from(this.querySelectorAll('[data-gp-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-gp-panel]'));
    this.images = Array.from(this.querySelectorAll('[data-gp-image]'));
    this.dots = Array.from(this.querySelectorAll('[data-gp-dot]'));
    this.slides = Array.from(this.querySelectorAll('[data-gp-card]'));
    this.track = this.querySelector('[data-gp-track]');
    if (this.tabs.length < 2 || this._wired) return;
    this._wired = true;
    this._index = 0;
    this._scrollLockUntil = 0;

    /* cards track scroll -> pack selection: pick the card nearest the
       scrollport centre once scrolling settles (timeout debounce — rAF can
       starve in background documents); ignore echoes of our own smooth scroll */
    if (this.track) {
      let settle = 0;
      this.track.addEventListener('scroll', () => {
        if (!GP_MOBILE.matches) return;
        clearTimeout(settle);
        settle = setTimeout(() => {
          if (Date.now() < this._scrollLockUntil) return;
          const idx = this.nearestSlide();
          if (idx !== this._index) this.select(idx, { scroll: false });
        }, 80);
      }, { passive: true });

      /* one-time discoverability nudge when the carousel first scrolls into
         view: glide 24px and settle back (skipped for reduced motion) */
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        const nudgeIo = new IntersectionObserver((entries) => {
          if (!entries.some((en) => en.isIntersecting)) return;
          nudgeIo.disconnect();
          if (!GP_MOBILE.matches || this.track.scrollLeft > 0) return;
          this._scrollLockUntil = Date.now() + 900;
          this.track.scrollBy({ left: 24, behavior: 'smooth' });
          setTimeout(() => this.track.scrollBy({ left: -24, behavior: 'smooth' }), 400);
        }, { threshold: 0.4 });
        nudgeIo.observe(this.track);
      }
    }

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.select(Number(tab.dataset.gpTab)));
      tab.addEventListener('keydown', (e) => this.onKeydown(e, tab));
    });

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.select(Number(dot.dataset.gpDot)));
    });
    this.querySelector('[data-gp-prev]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-gp-next]')?.addEventListener('click', () => this.step(1));
  }

  step(delta) {
    const n = this.panels.length;
    this.select((this._index + delta + n) % n);
  }

  nearestSlide() {
    const centre = this.track.scrollLeft + this.track.clientWidth / 2;
    let best = 0, bestDist = Infinity;
    this.slides.forEach((card) => {
      const dist = Math.abs(card.offsetLeft + card.clientWidth / 2 - centre);
      if (dist < bestDist) { bestDist = dist; best = Number(card.dataset.gpCard); }
    });
    return best;
  }

  onKeydown(e, tab) {
    const i = this.tabs.indexOf(tab);
    let next = null;
    if (e.key === 'ArrowRight') next = (i + 1) % this.tabs.length;
    else if (e.key === 'ArrowLeft') next = (i - 1 + this.tabs.length) % this.tabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = this.tabs.length - 1;
    if (next === null) return;
    e.preventDefault();
    this.select(next);
    this.tabs[next].focus();
  }

  select(index, opts = {}) {
    this._index = index;
    /* keep the mobile carousel in step with dot/chevron selections */
    if (opts.scroll !== false && this.track && GP_MOBILE.matches) {
      const card = this.slides.find((el) => Number(el.dataset.gpCard) === index);
      if (card) {
        this._scrollLockUntil = Date.now() + 700;
        this.track.scrollTo({
          left: card.offsetLeft + card.clientWidth / 2 - this.track.clientWidth / 2,
          behavior: 'smooth',
        });
      }
    }
    this.dots.forEach((dot, i) => {
      const active = i === index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      if (active) tab.removeAttribute('tabindex');
      else tab.setAttribute('tabindex', '-1');
    });
    this.panels.forEach((panel, i) => {
      const active = i === index;
      panel.hidden = !active;
      panel.classList.toggle('is-entering', active);
    });
    this.images.forEach((img) => {
      img.classList.toggle('is-active', Number(img.dataset.gpImage) === index);
    });
  }
}

if (!customElements.get('s1-gift-packs')) {
  customElements.define('s1-gift-packs', S1GiftPacks);
}
